"use client";

import { Sparkles } from "lucide-react";
import { usePathname } from "next/navigation";
import { useTranslation } from "@/lib/i18n";
import { useConversation } from "@/lib/chat/conversation";
import {
  followUpSuggestions,
  openingSuggestions,
} from "@/lib/chat/suggestions";

/**
 * Example questions when the conversation is empty, follow-ups after an answer.
 * Both render in the conversation area — the slot the conversation itself will
 * occupy — so the panel's anatomy never changes.
 *
 * Which questions are offered is chosen from the page the visitor is reading
 * and from what the last answer was about, so a visitor on the mobile-apps page
 * is not asked about workflow automation. See `src/lib/chat/suggestions.ts`.
 */
export default function Suggestions({ compact }: { compact: boolean }) {
  const { t, locale } = useTranslation();
  const pathname = usePathname();
  const { messages, isStreaming, send } = useConversation();

  const isEmpty = messages.length === 0;
  const last = messages[messages.length - 1];
  const lastIsSettledAnswer =
    !isStreaming &&
    messages.length > 0 &&
    last.role === "assistant" &&
    last.content.length > 0;

  if (isStreaming) return null;
  if (!isEmpty && !lastIsSettledAnswer) return null;
  // A contact form at the end of the conversation is the next step on offer,
  // and on a short panel the chips sit over its buttons. They return with the
  // next answer.
  if (last?.action?.kind === "contact") return null;

  const offered = isEmpty
    ? openingSuggestions(pathname, locale)
    : followUpSuggestions(
        last.content,
        pathname,
        locale,
        messages.filter((m) => m.role === "user").map((m) => m.content),
      );

  /*
   * The docked panel is 384 wide and these questions are whole sentences: every
   * chip wraps to two lines there, so the full opening set turns the empty
   * state into a wall of chips above the composer. Three is what fits before
   * that happens. The wide panel has the room and shows them all, and the
   * resting Ask-Bar types through the entire set in its placeholder either way,
   * so nothing in the set goes unseen.
   */
  const questions = compact ? offered.slice(0, 3) : offered;

  if (questions.length === 0) return null;

  /*
   * Gives way, like the message list, when the panel is short, e.g. above an
   * on-screen keyboard: it shrinks and scrolls rather than pushing the composer
   * and the recording notice out of the panel's clipped bottom. Where the panel
   * has the room, nothing shrinks.
   */
  return (
    <div
      className={`flex min-h-0 flex-col gap-3 overflow-y-auto bg-assistant-surface ${
        isEmpty ? "p-4" : compact ? "px-4 pb-4" : "px-5 pb-5"
      }`}
    >
      {/* Greeting only on a genuinely empty conversation — the resting Ask-Bar
          never reaches here, so this is the bubble/docked empty state. */}
      {isEmpty && (
        <div className="flex items-start gap-2.5">
          <div
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-assistant-divider bg-assistant-field"
            aria-hidden="true"
          >
            <Sparkles size={12} className="text-mint-text" />
          </div>
          <div className="flex min-w-0 flex-col gap-1">
            <p className="text-xs font-medium text-text-primary">
              {t("chat.welcome")}
            </p>
            <p className="text-[11px] leading-relaxed text-text-secondary">
              {t("chat.welcome.desc")}
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {questions.map((question) => (
          <button
            key={question}
            type="button"
            onClick={() => send(question)}
            className="rounded-2xl border border-assistant-field-border px-3.5 py-2 text-xs text-text-secondary transition-colors hover:border-mint-text/40 hover:bg-assistant-field"
          >
            {question}
          </button>
        ))}
      </div>
    </div>
  );
}
